/**
 * The Captains screen model (M6 spec §5, §8 "Captains: roster (hire and fire), assignments, wages, loyalty, XP, the
 * memorial"; plan P32). Pure: rows from the worker's `CaptainView` and `FleetView`, and a reducer for the screen's
 * focus, the reassign picker and the fire confirmation. The host carries out the effects.
 *
 * - **Rows:** every hired-pool captain (on duty, recovering, on strike, and a quit or deserted one until its slot
 *   is released) with portrait, state, assignment, wage per day, loyalty (warned at captainsScreen.json
 *   `loyaltyLow`) and XP (the skill closest to its next level; captains.json `xp.thresholds`).
 * - **Reassign (one captain per ship):** the picker offers *Unassign* (when the captain has a job) and every live
 *   ship without a captain. A ship that has a captain is never offered, so a reassign never displaces anyone (the
 *   worker would). A recovering or striking captain can only be unassigned (the worker's `busy`); a quit or
 *   deserted one has no actions.
 * - **Fire:** two presses: the first arms the row's button ("Confirm: fire …"), the second fires. Any other input
 *   disarms it.
 * - **Navigation (keyboard and pad):** a bar (Roster, Memorial, Close) and the list. Bar: left/right, down to the
 *   list, activate presses. List: up/down rows (up from the top returns to the bar), left/right switch between
 *   *Reassign* and *Fire*, activate presses. The picker: up/down, activate picks, back or left cancels. Back
 *   elsewhere closes the screen.
 */
import { z } from 'zod'
import captainsJson from '../../../data/strategic/captains.json'
import {
  CAPTAIN_STATE_NAMES,
  FACTION_IDS,
  SKILL_NAMES,
  type CaptainStateName,
  type CaptainSummary,
  type CaptainView,
  type FactionId,
  type FleetShipView,
  type FleetView,
  type SkillName,
} from '../../../strategic/types'
import { formatCr } from '../../format'
import type { NavInput } from '../../strategic/mount'
import configJson from '../captainsScreen.json'
import { panelCatalog, type PanelCatalog } from './catalog'
import type { MemorialNames, MemorialRow } from './memorial'
import { portraitRecipe, type PortraitRecipe } from './portrait'
import { skillsLine, traitsLine } from './roster'

// ---------------------------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------------------------

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/)
const tintSchema = z.strictObject({ bg: hex, fg: hex })

const configSchema = z.strictObject({
  comment: z.string(),
  refreshS: z.number().positive(),
  loyaltyLow: z.number().min(0).max(100),
  stateLabels: z.strictObject(Object.fromEntries(CAPTAIN_STATE_NAMES.map((s) => [s, z.string().min(1)])) as Record<CaptainStateName, z.ZodString>),
  causeUnknown: z.string().min(1),
  causeKilledBy: z.string().includes('{faction}'),
  rosterEmpty: z.string().min(1),
  memorialEmpty: z.string().min(1),
  tints: z.strictObject(Object.fromEntries(FACTION_IDS.map((f) => [f, tintSchema])) as Record<FactionId, typeof tintSchema>),
  memorialTint: tintSchema,
})

export type CaptainsConfig = z.infer<typeof configSchema> & { xpThresholds: readonly number[]; skillMax: number }

const captainsDataSchema = z.object({
  skills: z.object({ max: z.number().int() }),
  xp: z.object({ thresholds: z.array(z.number().positive()) }),
})

let cachedConfig: CaptainsConfig | null = null

/** captainsScreen.json plus captains.json's XP thresholds and skill cap (parsed once). */
export function loadCaptainsConfig(): CaptainsConfig {
  if (cachedConfig === null) {
    const data = captainsDataSchema.parse(captainsJson)
    cachedConfig = Object.freeze({ ...configSchema.parse(configJson), xpThresholds: Object.freeze([...data.xp.thresholds]), skillMax: data.skills.max })
  }
  return cachedConfig
}

// ---------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------

export interface CaptainNames extends MemorialNames {
  /** A player station's (commission `pst-<seq>`) name. */
  station(id: string): string
}

export interface CaptainRow {
  id: string
  name: string
  portrait: PortraitRecipe
  state: CaptainStateName
  stateLabel: string
  /** "Commands the Lantern", "Manages Varos Works", "Unassigned". */
  assignment: string
  shipId: string | null
  stationId: string | null
  wage: number
  wageLabel: string
  loyalty: number
  loyaltyLow: boolean
  xpTotal: number
  /** "Next: CBT 3 → 4 · 340/500 XP", or "Every skill at the top". */
  xpLabel: string
  skills: string
  traits: string
  canFire: boolean
  /** True when the reassign picker has something to offer. */
  canReassign: boolean
  /** Why the picker is closed ('' when it opens). */
  reassignReason: string
}

/** One choice in the reassign picker: a ship id, or null to unassign. */
export interface AssignOption {
  shipId: string | null
  label: string
}

/** A hired captain can be fired; quit and deserted ones are leaving the pool on their own. */
const FIREABLE: ReadonlySet<CaptainStateName> = new Set(['hired', 'recovering', 'striking'])

function stateLabel(c: CaptainSummary, cfg: CaptainsConfig): string {
  const base = cfg.stateLabels[c.state]
  return c.state === 'recovering' && c.recoverUntilDay !== null ? `${base} until day ${c.recoverUntilDay}` : base
}

/** The XP line: the skill closest to its next level (ties: the spec's skill order). */
export function xpLine(c: CaptainSummary, cfg: CaptainsConfig, cat: PanelCatalog = panelCatalog()): string {
  let best: SkillName | null = null
  let bestFrac = -1
  for (const s of SKILL_NAMES) {
    const level = c.skills[s]
    if (level >= cfg.skillMax) continue
    const need = cfg.xpThresholds[level - 1]
    if (need === undefined) continue
    const frac = c.xp[s] / need
    if (frac > bestFrac) {
      bestFrac = frac
      best = s
    }
  }
  if (best === null) return 'Every skill at the top'
  const level = c.skills[best]
  return `Next: ${cat.config.skillShort[best]} ${level} → ${level + 1} · ${Math.floor(c.xp[best])}/${cfg.xpThresholds[level - 1]} XP`
}

function xpTotal(c: CaptainSummary): number {
  let n = 0
  for (const s of SKILL_NAMES) n += c.xp[s]
  return n
}

/** Live ships without a captain (one captain per ship), in fleet order. */
export function freeShips(ships: readonly FleetShipView[]): FleetShipView[] {
  return ships.filter((s) => s.captain === null && s.status !== 'lost')
}

function shipLabel(s: FleetShipView, cat: PanelCatalog): string {
  return `${s.name} (${cat.config.classLabels[s.shipClass]})`
}

/** The reassign picker's choices for captain row `c`: Unassign (with a job), then free ships (on duty only). */
export function assignOptions(c: CaptainRow, ships: readonly FleetShipView[], cat: PanelCatalog = panelCatalog()): AssignOption[] {
  if (!FIREABLE.has(c.state)) return []
  const out: AssignOption[] = []
  if (c.shipId !== null || c.stationId !== null) out.push({ shipId: null, label: 'Unassign' })
  if (c.state === 'hired') for (const s of freeShips(ships)) out.push({ shipId: s.id, label: `Command the ${shipLabel(s, cat)}` })
  return out
}

function reassignReason(c: CaptainSummary, hasFree: boolean): string {
  if (!FIREABLE.has(c.state)) return `${c.name} is leaving your service.`
  const hasJob = c.shipId !== null || c.stationId !== null
  if (c.state !== 'hired') return hasJob ? '' : `${c.name} cannot take a ship while ${c.state === 'striking' ? 'on strike' : 'recovering'}.`
  return hasJob || hasFree ? '' : 'Every ship has a captain.'
}

export function captainRows(view: Pick<CaptainView, 'hired'>, fleet: Pick<FleetView, 'ships'>, names: CaptainNames, cfg: CaptainsConfig, cat: PanelCatalog = panelCatalog()): CaptainRow[] {
  const shipName = new Map(fleet.ships.map((s) => [s.id, s.name]))
  const hasFree = freeShips(fleet.ships).length > 0
  return view.hired.map((c): CaptainRow => {
    const assignment =
      c.shipId !== null ? `Commands the ${shipName.get(c.shipId) ?? c.shipId}` : c.stationId !== null ? `Manages ${names.station(c.stationId)}` : 'Unassigned'
    const reason = reassignReason(c, hasFree)
    return {
      id: c.id,
      name: c.name,
      portrait: portraitRecipe(c.name, c.portrait, cfg.tints[c.origin]),
      state: c.state,
      stateLabel: stateLabel(c, cfg),
      assignment,
      shipId: c.shipId,
      stationId: c.stationId,
      wage: c.wage,
      wageLabel: `${formatCr(Math.round(c.wage))}/day`,
      loyalty: Math.round(c.loyalty),
      loyaltyLow: c.loyalty <= cfg.loyaltyLow,
      xpTotal: Math.floor(xpTotal(c)),
      xpLabel: xpLine(c, cfg, cat),
      skills: skillsLine(c, cat),
      traits: traitsLine(c),
      canFire: FIREABLE.has(c.state),
      canReassign: reason === '',
      reassignReason: reason,
    }
  })
}

/** "Captains 3/64 · wages 1,260 cr/day from the wallet". */
export function summaryLine(rows: readonly CaptainRow[], hiredCap: number, soleTrader: boolean): string {
  let wages = 0
  for (const r of rows) if (FIREABLE.has(r.state)) wages += r.wage
  return `Captains ${rows.length}/${hiredCap} · wages ${formatCr(Math.round(wages))}/day from the ${soleTrader ? 'wallet' : 'company account'}`
}

// ---------------------------------------------------------------------------------------------
// Screen state and reducer
// ---------------------------------------------------------------------------------------------

export type CaptainsTab = 'roster' | 'memorial'
export const BAR = ['view:roster', 'view:memorial'] as const
export type BarControl = (typeof BAR)[number]
export type RowColumn = 'assign' | 'fire'

export type CaptainsFocus = { zone: 'bar'; index: number } | { zone: 'list'; index: number; col: RowColumn }

export interface AssignPicker {
  captainId: string
  options: AssignOption[]
  index: number
}

export interface CaptainsScreenState {
  tab: CaptainsTab
  rows: CaptainRow[]
  memorial: MemorialRow[]
  ships: readonly FleetShipView[]
  hiredCap: number
  soleTrader: boolean
  focus: CaptainsFocus
  picker: AssignPicker | null
  /** The captain whose Fire button is armed. */
  confirmFire: string | null
  notice: string | null
}

export type CaptainsAction =
  | { type: 'data'; rows: CaptainRow[]; memorial: MemorialRow[]; ships: readonly FleetShipView[]; hiredCap: number; soleTrader: boolean }
  | { type: 'nav'; input: NavInput }
  | { type: 'cycleFocus'; dir: 1 | -1 }
  | { type: 'press'; control: BarControl }
  /** A click on a row's button (the pointer's way in): focuses it, then presses it. */
  | { type: 'pressRow'; index: number; col: RowColumn }
  /** A click on a picker choice. */
  | { type: 'pick'; index: number }
  | { type: 'notice'; text: string | null }

export type CaptainsEffect =
  | { kind: 'assign'; captainId: string; name: string; shipId: string | null; label: string }
  | { kind: 'fire'; captainId: string; name: string }
  | { kind: 'close' }

export interface CaptainsResult {
  state: CaptainsScreenState
  effect: CaptainsEffect | null
}

export function createCaptainsState(): CaptainsScreenState {
  return {
    tab: 'roster',
    rows: [],
    memorial: [],
    ships: [],
    hiredCap: 0,
    soleTrader: true,
    focus: { zone: 'bar', index: 0 },
    picker: null,
    confirmFire: null,
    notice: null,
  }
}

function listLength(s: CaptainsScreenState): number {
  return s.tab === 'roster' ? s.rows.length : s.memorial.length
}

function out(state: CaptainsScreenState, effect: CaptainsEffect | null = null): CaptainsResult {
  return { state, effect }
}

function clamp(i: number, n: number): number {
  return Math.max(0, Math.min(n - 1, i))
}

/** The captain row under the list focus (roster tab), or null. */
export function focusedRow(s: CaptainsScreenState): CaptainRow | null {
  if (s.tab !== 'roster' || s.focus.zone !== 'list') return null
  return s.rows[s.focus.index] ?? null
}

function pressBar(s: CaptainsScreenState, c: BarControl): CaptainsResult {
  const index = BAR.indexOf(c)
  switch (c) {
    case 'view:roster':
    case 'view:memorial':
      return out({ ...s, tab: c === 'view:roster' ? 'roster' : 'memorial', focus: { zone: 'bar', index }, picker: null, confirmFire: null, notice: null })
  }
}

function pressRow(s: CaptainsScreenState, index: number, col: RowColumn): CaptainsResult {
  if (s.tab !== 'roster') return out(s)
  const row = s.rows[index]
  if (row === undefined) return out(s)
  const focus: CaptainsFocus = { zone: 'list', index, col }
  if (col === 'fire') {
    if (!row.canFire) return out({ ...s, focus, confirmFire: null, notice: `${row.name} is already leaving your service.` })
    if (s.confirmFire === row.id) return out({ ...s, focus, confirmFire: null }, { kind: 'fire', captainId: row.id, name: row.name })
    return out({ ...s, focus, confirmFire: row.id, notice: `Press again to fire ${row.name}.` })
  }
  const options = assignOptions(row, s.ships)
  if (!row.canReassign || options.length === 0) return out({ ...s, focus, confirmFire: null, notice: row.reassignReason === '' ? 'Every ship has a captain.' : row.reassignReason })
  return out({ ...s, focus, confirmFire: null, notice: null, picker: { captainId: row.id, options, index: 0 } })
}

function pick(s: CaptainsScreenState, index: number): CaptainsResult {
  const p = s.picker
  if (p === null) return out(s)
  const o = p.options[index]
  const row = s.rows.find((r) => r.id === p.captainId)
  if (o === undefined || row === undefined) return out({ ...s, picker: null })
  return out({ ...s, picker: null }, { kind: 'assign', captainId: row.id, name: row.name, shipId: o.shipId, label: o.label })
}

function navPicker(s: CaptainsScreenState, p: AssignPicker, input: NavInput): CaptainsResult {
  switch (input) {
    case 'up':
    case 'down': {
      const n = p.options.length
      const index = (p.index + (input === 'down' ? 1 : -1) + n) % n
      return out({ ...s, picker: { ...p, index } })
    }
    case 'activate':
      return pick(s, p.index)
    case 'back':
    case 'left':
    case 'start':
      return out({ ...s, picker: null })
    case 'right':
      return out(s)
  }
}

function navBar(s: CaptainsScreenState, index: number, input: NavInput): CaptainsResult {
  const n = listLength(s)
  switch (input) {
    case 'left':
    case 'right':
      return out({ ...s, focus: { zone: 'bar', index: clamp(index + (input === 'right' ? 1 : -1), BAR.length) } })
    case 'down':
      return n === 0 ? out(s) : out({ ...s, focus: { zone: 'list', index: 0, col: 'assign' } })
    case 'up':
      return out(s)
    case 'activate':
      return pressBar(s, BAR[index])
    case 'back':
    case 'start':
      return out(s, { kind: 'close' })
  }
}

function navList(s: CaptainsScreenState, f: Extract<CaptainsFocus, { zone: 'list' }>, input: NavInput): CaptainsResult {
  const n = listLength(s)
  const disarmed = { ...s, confirmFire: null }
  switch (input) {
    case 'up':
      return f.index === 0 ? out({ ...disarmed, focus: { zone: 'bar', index: BAR.indexOf(s.tab === 'roster' ? 'view:roster' : 'view:memorial') } }) : out({ ...disarmed, focus: { ...f, index: f.index - 1 } })
    case 'down':
      return out({ ...disarmed, focus: { ...f, index: clamp(f.index + 1, n) } })
    case 'left':
    case 'right':
      return s.tab === 'roster' ? out({ ...disarmed, focus: { ...f, col: input === 'right' ? 'fire' : 'assign' } }) : out(s)
    case 'activate':
      return pressRow(s, f.index, f.col)
    case 'back':
    case 'start':
      return out(disarmed, { kind: 'close' })
  }
}

/** Keeps the list focus on the same captain across a refresh (or the same index when it left). */
function refocus(prev: CaptainsScreenState, next: CaptainsScreenState): CaptainsFocus {
  const f = prev.focus
  if (f.zone === 'bar') return f
  const n = listLength(next)
  if (n === 0) return { zone: 'bar', index: BAR.indexOf(next.tab === 'roster' ? 'view:roster' : 'view:memorial') }
  if (next.tab === 'roster') {
    const id = prev.rows[f.index]?.id
    const j = id === undefined ? -1 : next.rows.findIndex((r) => r.id === id)
    if (j >= 0) return { ...f, index: j }
  }
  return { ...f, index: clamp(f.index, n) }
}

export function reduceCaptains(s: CaptainsScreenState, a: CaptainsAction): CaptainsResult {
  switch (a.type) {
    case 'data': {
      const next: CaptainsScreenState = { ...s, rows: a.rows, memorial: a.memorial, ships: a.ships, hiredCap: a.hiredCap, soleTrader: a.soleTrader }
      const focus = refocus(s, next)
      // A picker or an armed Fire whose captain left (or can no longer act) is dropped.
      const pickerRow = s.picker === null ? undefined : a.rows.find((r) => r.id === s.picker?.captainId)
      const picker = s.picker !== null && pickerRow !== undefined && pickerRow.canReassign ? { ...s.picker, options: assignOptions(pickerRow, a.ships) } : null
      const keepPicker = picker !== null && picker.options.length > 0 ? { ...picker, index: clamp(picker.index, picker.options.length) } : null
      const confirmFire = s.confirmFire !== null && a.rows.some((r) => r.id === s.confirmFire && r.canFire) ? s.confirmFire : null
      return out({ ...next, focus, picker: keepPicker, confirmFire })
    }
    case 'nav': {
      if (s.picker !== null) return navPicker(s, s.picker, a.input)
      const f = s.focus
      return f.zone === 'bar' ? navBar(s, f.index, a.input) : navList(s, f, a.input)
    }
    case 'cycleFocus': {
      if (s.picker !== null) return out(s)
      const f = s.focus
      if (f.zone === 'list') return out({ ...s, confirmFire: null, focus: { zone: 'bar', index: 0 } })
      return listLength(s) === 0 ? out(s) : out({ ...s, focus: { zone: 'list', index: 0, col: 'assign' } })
    }
    case 'press':
      return s.picker !== null ? out(s) : pressBar(s, a.control)
    case 'pressRow':
      return s.picker !== null ? out(s) : pressRow(s, a.index, a.col)
    case 'pick':
      return pick(s, a.index)
    case 'notice':
      return out({ ...s, notice: a.text })
  }
}

/** The line after a reassign: "Ilse Varga now commands the Lantern." / "Ilse Varga is unassigned." */
export function assignedText(name: string, shipId: string | null, ships: readonly FleetShipView[]): string {
  if (shipId === null) return `${name} is unassigned.`
  const ship = ships.find((s) => s.id === shipId)
  return `${name} now commands the ${ship?.name ?? shipId}.`
}
